import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { OfferCarousel } from 'atomic/organisms/offer/OfferCarousel';
import { OfferCarouselPlaceholder } from 'atomic/organisms/offer/OfferCarousel/OfferCarouselPlaceholder';

import { imgSize800, imgSize1600 } from 'constants/image';
import { useOfferFavorite } from 'hooks/useFavorite';
import { useImagesPreloader } from 'hooks/useImagesPreloader';
import { formatImagelink } from 'utils';

import { analytics } from '@Modules/analytics/analytics';
import { selectIsChatWidgetOpened } from '@Modules/chat/selectors';
import type { OfferType } from '@Modules/offers/types';

import { NoSsr } from '@Atomic/organisms/NoSsr';
import { OfferSeller } from '@Atomic/organisms/offer/OfferSeller';
import { OfferStatsToast } from '@Atomic/organisms/offer/OfferStatsToast';
import { SellerContact } from '@Atomic/organisms/SellerContact';
import { FullScreenGallery } from '@Atomic/molecules/FullscreenGallery';

import styles from './offerGallery.module.scss';

type RenderGalleryType = {
  offer: OfferType;
  isMobile?: boolean;
  isExpired?: boolean;
};

export const OfferGallery = ({ offer, isMobile, isExpired }: RenderGalleryType) => {
  const [fullScreenGalleryVisible, setFullScreenGalleryVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { checkIsFavorite } = useOfferFavorite();
  const isChatWidgetOpen = useSelector(selectIsChatWidgetOpened);

  useEffect(() => {
    if (isChatWidgetOpen) {
      setFullScreenGalleryVisible(false);
    }
  }, [isChatWidgetOpen]);

  const images = useMemo(
    () =>
      offer.photos.map(({ upload, id, alt }) => ({
        id,
        alt: alt || offer.title,
        image: formatImagelink(upload.cropUrlTemplate, imgSize800),
        imageBig: formatImagelink(upload.cropUrlTemplate, imgSize1600),
      })),
    [offer]
  );

  // Fullscreen photos are fetched on open: on load they competed with the LCP photo
  const imagesSrcList = useMemo(
    () =>
      fullScreenGalleryVisible && images.length > 0
        ? [images[activeIndex].imageBig, ...images.map((i) => i.image)]
        : [],
    [activeIndex, fullScreenGalleryVisible, images]
  );

  const { assetsLoaded } = useImagesPreloader(imagesSrcList);

  if (offer.photos.length === 0) {
    return null;
  }

  const handleSlideClick = () => {
    setFullScreenGalleryVisible(true);
  };

  const handleSlideChange = (activeIndex: number, previousIndex: number) => {
    setActiveIndex(activeIndex);
    analytics.offerPhotoSlide({
      offer_id: offer.id,
      photo_from_num: previousIndex + 1,
      photo_to_num: activeIndex + 1,
      photos_count: images.length,
      is_favorite: checkIsFavorite(offer.id, offer.favorited),
    });
  };

  return (
    <NoSsr
      placeholder={
        <OfferCarouselPlaceholder
          images={images}
          primaryId={offer.primaryPhoto?.id}
          isExpired={isExpired}
          testId="offer_carousel"
        />
      }
    >
      <>
        <OfferCarousel
          key={offer.id}
          images={images}
          isMobile={isMobile}
          isExpired={isExpired}
          testId="offer_carousel"
          primaryId={offer.primaryPhoto?.id}
          onSlideChange={handleSlideChange}
          onClick={handleSlideClick}
          overlay={<OfferStatsToast toast={offer.toast} />}
        />

        <FullScreenGallery
          loading={!assetsLoaded}
          images={images}
          defaultActiveIndex={activeIndex}
          onClose={() => setFullScreenGalleryVisible(false)}
          visible={fullScreenGalleryVisible}
          footer={
            <>
              <OfferSeller variant="horizontal-reverse" seller={offer.seller} withLinks />
              <SellerContact
                testId="offer_gallery_contact"
                className={styles.contacts}
                buttonClassName={styles.contactButton}
                offerId={offer.id}
                seller={offer.seller}
                contactViaApp={offer.contactViaApp}
              />
            </>
          }
        />
      </>
    </NoSsr>
  );
};
